import { useCallback, useMemo } from 'react';
import { labelsJsonSchema } from '@/api/schemas';
import { getImpactQuery } from '@/components/Dashboard/queries';

// Founder decision 2026-09-06: the front end owns rendering only - every label,
// caption, unit and card second line is data. There is NO separate labels
// layer: the labels ride on the headline row this app already fetches,
// key_impact_data_live.labels_json, a JSON string of { key: value } such as
// {"headline.money_left_at_origin.caption": "since 2012"}. Components read
// them through t(key, fallback).
//
// The fallback is the exact text production showed before PR #7, so with the
// property absent (today's production reality until the data side fills it),
// empty, malformed, or the headline request failing, the site looks like pre-PR
// production. Caption keys pass '' as their fallback and the caller renders
// nothing for an empty string.

export type LabelLookup = (key: string, fallback: string) => string;

// Parse + validate labels_json once per distinct value (§8: untrusted until it
// passes the schema). Absence is silent - it is the normal state for now. A
// malformed value is logged with enough context to diagnose from the console
// and yields an empty map, i.e. the fallback text, never a half-applied set.
export const parseLabelsJson = (raw: unknown): Map<string, string> => {
  const map = new Map<string, string>();
  if (raw == null || raw === '') return map;
  if (typeof raw !== 'string') {
    console.error(
      '[boundary] key_impact_data_live.labels_json: expected a JSON string, got',
      typeof raw,
    );
    return map;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    console.error(
      '[boundary] key_impact_data_live.labels_json: JSON.parse failed',
      e,
      raw.slice(0, 200),
    );
    return map;
  }
  const result = labelsJsonSchema.safeParse(parsed);
  if (!result.success) {
    console.error(
      '[boundary] key_impact_data_live.labels_json: not a flat { key: string } object',
      result.error.issues.slice(0, 5).map((i) => ({ path: i.path.join('.'), message: i.message })),
    );
    return map;
  }
  for (const [key, value] of Object.entries(result.data)) {
    // A null/blank value is "no label", not an empty label: the fallback wins.
    if (typeof value === 'string' && value.trim() !== '') map.set(key, value);
  }
  return map;
};

export const useLabels = () => {
  const { data, isLoading, isError } = getImpactQuery();
  const raw: unknown = data?.features?.[0]?.properties?.labels_json;
  const map = useMemo(() => parseLabelsJson(raw), [raw]);

  const t: LabelLookup = useCallback((key, fallback) => map.get(key) ?? fallback, [map]);

  // 'data' only when at least one label arrived; lets the UI/tests tell a served
  // label from a fallback without inspecting text.
  const source: 'data' | 'fallback' = !isError && map.size > 0 ? 'data' : 'fallback';

  return { t, isLoading, source };
};
